import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Upload, X, RotateCcw, ImageOff, Link as LinkIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const ACCEPT = "image/jpeg,image/png,image/webp";
const ACCEPT_EXT = [".jpg", ".jpeg", ".png", ".webp"];
const MAX_MB = 8;

/**
 * Reusable image uploader with drag/drop, mobile-friendly file picker, preview,
 * replace, and remove. Falls back to a paste-URL toggle for advanced users.
 *
 * Props:
 *  - value: current image URL
 *  - onChange(url): called with the new URL after successful upload or manual paste
 *  - onRemove(): optional; called when user removes
 *  - scope: "artist" | "event" | "fan" | "admin"
 *  - artistId: required for scope=artist|event
 *  - aspect: "square" | "video" (default "square")
 *  - testId: prefix for data-testid attributes
 *  - label: optional label above uploader (or handled by parent FieldWrap)
 */
export default function ImageUploader({
    value = "",
    onChange,
    onRemove,
    scope,
    artistId,
    aspect = "square",
    testId = "image",
    label,
}) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [preview, setPreview] = useState(value);
    const [dragActive, setDragActive] = useState(false);
    const [urlMode, setUrlMode] = useState(false);
    const [urlDraft, setUrlDraft] = useState(value);

    useEffect(() => {
        setPreview(value);
        setUrlDraft(value);
    }, [value]);

    const openPicker = () => inputRef.current?.click();

    const handleFiles = async (files) => {
        const file = files && files[0];
        if (!file) return;
        // MIME check
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Unsupported file type. Use JPEG, PNG, or WebP.");
            return;
        }
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`File too large. Max ${MAX_MB} MB.`);
            return;
        }
        // Optimistic local preview while uploading
        const localUrl = URL.createObjectURL(file);
        setPreview(localUrl);
        setUploading(true);
        setProgress(0);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("scope", scope);
            if (artistId) form.append("artist_id", artistId);
            const r = await axios.post(`${API}/uploads/image`, form, {
                withCredentials: true,
                headers: {
                    Authorization: axios.defaults.headers.common.Authorization,
                    "Content-Type": "multipart/form-data",
                },
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
                },
            });
            const url = r.data?.url;
            if (!url) throw new Error("Upload returned no URL");
            URL.revokeObjectURL(localUrl);
            setPreview(url);
            onChange && onChange(url);
            toast.success("Image uploaded");
        } catch (e) {
            URL.revokeObjectURL(localUrl);
            setPreview(value); // revert
            const msg = e?.response?.data?.detail || e?.message || "Upload failed";
            toast.error(typeof msg === "string" ? msg : "Upload failed");
        } finally {
            setUploading(false);
            setProgress(0);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        if (uploading) return;
        handleFiles(e.dataTransfer.files);
    };

    const remove = () => {
        setPreview("");
        onChange && onChange("");
        onRemove && onRemove();
    };

    const commitUrl = () => {
        const v = (urlDraft || "").trim();
        if (v && !/^https?:\/\//.test(v)) {
            toast.error("URL must start with http:// or https://");
            return;
        }
        setPreview(v);
        onChange && onChange(v);
        setUrlMode(false);
    };

    const aspectClass = aspect === "video" ? "aspect-video" : "aspect-square";

    return (
        <div className="space-y-2" data-testid={`${testId}-uploader`}>
            {label && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{label}</div>
            )}
            <div
                onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={onDrop}
                className={`relative border ${dragActive ? "border-[#ff4400]" : "border-[#2a2a2a]"} bg-[#141414] transition-colors`}
            >
                <div className={`${aspectClass} w-full relative overflow-hidden`}>
                    {preview ? (
                        <img
                            src={preview}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover grayscale"
                            data-testid={`${testId}-preview`}
                            onError={() => setPreview("")}
                        />
                    ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
                            <ImageOff className="w-6 h-6 text-[#666]" />
                            <div className="text-[10px] font-mono uppercase text-[#666] leading-tight">
                                Drag a photo here<br />or tap upload below
                            </div>
                        </div>
                    )}
                    {uploading && (
                        <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-2" data-testid={`${testId}-uploading`}>
                            <Loader2 className="w-6 h-6 animate-spin text-[#ff4400]" />
                            <div className="text-[10px] font-mono uppercase text-[#f4f4f0]" data-testid={`${testId}-progress`}>
                                Uploading… {progress}%
                            </div>
                            <div className="w-2/3 h-1 bg-[#2a2a2a]">
                                <div className="h-full bg-[#ff4400] transition-all" style={{ width: `${progress}%` }} />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap gap-1">
                <button
                    type="button"
                    onClick={openPicker}
                    disabled={uploading}
                    className="pm-btn text-[10px]"
                    data-testid={`${testId}-upload-btn`}
                >
                    <Upload className="w-3 h-3 mr-1" /> {preview ? "Replace" : "Upload image"}
                </button>
                {preview && (
                    <button
                        type="button"
                        onClick={remove}
                        disabled={uploading}
                        className="pm-btn text-[10px] text-[#ff3333] border-[#ff3333]"
                        data-testid={`${testId}-remove-btn`}
                    >
                        <X className="w-3 h-3 mr-1" /> Remove
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => setUrlMode((m) => !m)}
                    disabled={uploading}
                    className="pm-btn text-[10px]"
                    data-testid={`${testId}-url-toggle`}
                >
                    <LinkIcon className="w-3 h-3 mr-1" /> {urlMode ? "Hide URL" : "Paste URL"}
                </button>
            </div>

            {urlMode && (
                <div className="flex gap-2" data-testid={`${testId}-url-row`}>
                    <input
                        value={urlDraft}
                        onChange={(e) => setUrlDraft(e.target.value)}
                        placeholder="https://…"
                        className="flex-1 bg-transparent border border-[#2a2a2a] px-3 py-2 text-sm focus:border-[#ff4400] outline-none"
                        data-testid={`${testId}-url-input`}
                    />
                    <button
                        type="button"
                        onClick={commitUrl}
                        className="pm-btn text-[10px]"
                        data-testid={`${testId}-url-apply`}
                    >
                        <RotateCcw className="w-3 h-3 mr-1" /> Apply
                    </button>
                </div>
            )}

            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT + "," + ACCEPT_EXT.join(",")}
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid={`${testId}-file-input`}
            />
        </div>
    );
}
